import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";

import {
  Box,
  Typography,
  Grid,
  Card,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Icon,
  Button,
  Chip,
} from "@mui/material";
import { getUserInfo } from "services/authService";
import { getAnnotatorsIncludingDeleted } from "services/userService";
import { getAllAnnotatorsByAdminIncludingDeleted } from "services/sampleService";
import { getDashboardBasicStats } from "services/dashboardService";
import { useTranslation } from "hooks/useTranslation";

// Annotator filter component with PropTypes
function AnnotatorFilter({ annotatorId, onAnnotatorChange, userRole }) {
  const { t } = useTranslation();
  const [annotators, setAnnotators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch annotators on component mount
  useEffect(() => {
    // Don't fetch annotators if user is an annotator
    if (userRole === "Annotator") {
      setLoading(false);
      return;
    }

    const fetchAnnotators = async () => {
      try {
        setLoading(true);
        setError(null);

        const userInfo = getUserInfo();
        let response;

        if (userRole === "SuperAdmin") {
          // Superadmin: Get all annotators
          response = await getAnnotatorsIncludingDeleted();
          setAnnotators(response);
        } else {
          // Regular admin: Get annotators created by this admin
          const adminId = userInfo?.id;
          response = await getAllAnnotatorsByAdminIncludingDeleted(adminId);
          setAnnotators(response);
        }
      } catch (error) {
        console.error("Failed to fetch annotators:", error);
        setError("Failed to load annotators");
        setAnnotators([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAnnotators();
  }, [userRole]);

  // Don't show filter for annotators
  if (userRole === "Annotator") {
    return null;
  }

  // Empty state for annotators
  if (annotators.length === 0 && !loading && !error) {
    return (
      <FormControl size="small" sx={{ minWidth: 120, mb: 2 }}>
        <Select value="" displayEmpty disabled>
          <MenuItem value="">
            {t("dashboard.noAnnotatorsAvailable", "No annotators available")}
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (loading) {
    return (
      <FormControl size="small" sx={{ minWidth: 120, mb: 2 }}>
        <Select value="" displayEmpty disabled>
          <MenuItem value="">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={16} />
              Loading annotators...
            </Box>
          </MenuItem>
        </Select>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl size="small" sx={{ minWidth: 120, mb: 2 }}>
        <Select value="" displayEmpty disabled>
          <MenuItem value="">Error loading annotators</MenuItem>
        </Select>
      </FormControl>
    );
  }

  return (
    <FormControl size="small" sx={{ minWidth: 200, mb: 2 }}>
      <Select
        value={annotatorId}
        onChange={(e) => onAnnotatorChange(e.target.value)}
        displayEmpty
        sx={{
          "& .MuiSelect-icon": { display: "block !important" },
        }}
        MenuProps={{
          disablePortal: false,
          style: { zIndex: 9999 },
          PaperProps: {
            sx: { backgroundColor: "white", maxHeight: 300 },
          },
        }}
      >
        <MenuItem value="">All Annotators</MenuItem>
        {annotators.map((annotator) => (
          <MenuItem key={annotator.id} value={annotator.id}>
            {annotator.first_name && annotator.last_name
              ? `${annotator.first_name} ${annotator.last_name} (${annotator.email_id})`
              : annotator.email_id || `Annotator ${annotator.id}`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// PropTypes for type checking
AnnotatorFilter.propTypes = {
  annotatorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onAnnotatorChange: PropTypes.func.isRequired,
  userRole: PropTypes.string.isRequired,
};

AnnotatorFilter.defaultProps = {
  annotatorId: "",
};

// Custom hook for lazy loading dashboard data
function useLazyDashboardStats(annotatorId, adminId) {
  const [stats, setStats] = useState({
    basic: null,
  });
  const [loading, setLoading] = useState({
    basic: false,
  });
  const [errors, setErrors] = useState({
    basic: null,
  });

  const fetchData = useCallback(async () => {
    const annotatorIdForApi = annotatorId || "";

    // Reset errors on every fetch
    setErrors({ basic: null });

    // Fetch basic stats
    setLoading((prev) => ({ ...prev, basic: true }));
    try {
      const basicData = await getDashboardBasicStats(annotatorIdForApi, adminId);
      setStats((prev) => ({ ...prev, basic: basicData }));
      setErrors((prev) => ({ ...prev, basic: null }));
    } catch (error) {
      console.error("Failed to fetch basic stats:", error);
      setErrors((prev) => ({ ...prev, basic: error.message }));
    } finally {
      setLoading((prev) => ({ ...prev, basic: false }));
    }
  }, [annotatorId, adminId]);

  const retry = useCallback(
    (type) => {
      if (type === "all") {
        fetchData();
      } else {
        setErrors((prev) => ({ ...prev, [type]: null }));
        setLoading((prev) => ({ ...prev, [type]: true }));

        const fetchFunctions = {
          basic: getDashboardBasicStats,
        };

        fetchFunctions[type](annotatorId || "", adminId)
          .then((data) => {
            setStats((prev) => ({ ...prev, [type]: data }));
            setErrors((prev) => ({ ...prev, [type]: null }));
          })
          .catch((error) => {
            console.error(`Failed to fetch ${type}:`, error);
            setErrors((prev) => ({ ...prev, [type]: error.message }));
          })
          .finally(() => {
            setLoading((prev) => ({ ...prev, [type]: false }));
          });
      }
    },
    [annotatorId, adminId, fetchData]
  );

  return {
    stats,
    loading,
    errors,
    fetchData,
    retry,
  };
}

// Main Dashboard Component (without LanguageProvider)
function DashboardContent() {
  const { t } = useTranslation();
  const [userInfo] = useState(() => getUserInfo());
  const [selectedAnnotator, setSelectedAnnotator] = useState(() => {
    const user = getUserInfo();
    return user?.role === "Annotator" && user?.id ? user.id.toString() : "";
  });

  // Determine which annotator ID to pass to the hook
  const getAnnotatorIdForApi = () => {
    // If user is an annotator, always pass their ID
    if (userInfo?.role === "Annotator" && userInfo?.id) {
      return userInfo.id.toString();
    }
    // For other roles, pass the selected annotator (could be empty for "All Annotators")
    return selectedAnnotator;
  };

  // Get adminId if user role is Admin
  const getAdminId = () => {
    // Only pass adminId if user role is "Admin" (not "SuperAdmin" or "Annotator")
    if (userInfo?.role === "Admin" && userInfo?.id) {
      return userInfo.id.toString();
    }
    return undefined;
  };

  // Use the appropriate annotator ID for API calls
  const annotatorIdForApi = getAnnotatorIdForApi();
  const adminIdForApi = getAdminId();
  const { stats, loading, errors, fetchData, retry } = useLazyDashboardStats(
    annotatorIdForApi,
    adminIdForApi
  );

  // Fetch data when annotator changes
  useEffect(() => {
    if (userInfo) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annotatorIdForApi]);

  // Handle manual refresh
  const handleRefresh = () => {
    fetchData();
  };

  // Handle annotator change (only for non-annotators)
  const handleAnnotatorChange = (annotatorId) => {
    if (userInfo?.role !== "Annotator") {
      setSelectedAnnotator(annotatorId);
    }
  };

  // Check if current user is an annotator OR if a specific annotator is selected
  const isAnnotator =
    userInfo?.role === "Annotator" ||
    (selectedAnnotator !== "" && selectedAnnotator !== null && selectedAnnotator !== undefined);

  // Check if any data is loading
  const anyLoading = loading.basic;
  const anyError = errors.basic;

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox pt={6} pb={3} px={3}>
        {/* Header with user role and filter info */}
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {t("navigation.dashboard")}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            {/* Status Chips */}
            <Box display="flex" gap={1}>
              {loading.basic && <Chip label="Stats" size="small" color="info" />}
              {errors.basic && <Chip label="Stats Error" size="small" color="error" />}
            </Box>

            {/* Annotator Filter - Only show for Admin/SuperAdmin */}
            {userInfo?.role !== "Annotator" && (
              <AnnotatorFilter
                annotatorId={selectedAnnotator}
                onAnnotatorChange={handleAnnotatorChange}
                userRole={userInfo?.role}
              />
            )}
          </Box>
        </MDBox>

        {/* Global Error Alert */}
        {anyError && (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => retry("all")}>
                Retry
              </Button>
            }
            sx={{ mb: 2 }}
          >
            Failed to load dashboard data. Please try again.
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Top stat cards */}
          <Grid item xs={12} md={isAnnotator ? 3 : 2.4} lg={isAnnotator ? 3 : 2.4}>
            {errors.basic ? (
              <Card>
                <MDBox p={3} textAlign="center">
                  <Typography color="error">Failed to load stats</Typography>
                  <Button
                    size="small"
                    onClick={() => retry("basic")}
                    disabled={loading.basic}
                    sx={{ mt: 1 }}
                  >
                    {loading.basic ? <CircularProgress size={16} /> : "Retry"}
                  </Button>
                </MDBox>
              </Card>
            ) : (
              <ComplexStatisticsCard
                color="info"
                title={t("dashboard.ttlAssigned")}
                icon={<Icon fontSize="large">assignment</Icon>}
                count={stats.basic?.total_assigned?.toString() || "0"}
                percentage={{ color: "success", amount: "", label: "" }}
                isLoading={loading.basic}
              />
            )}
          </Grid>

          <Grid item xs={12} md={isAnnotator ? 3 : 2.4} lg={isAnnotator ? 3 : 2.4}>
            {errors.basic ? (
              <Card>
                <MDBox p={3} textAlign="center">
                  <Typography color="error">Failed to load stats</Typography>
                  <Button
                    size="small"
                    onClick={() => retry("basic")}
                    disabled={loading.basic}
                    sx={{ mt: 1 }}
                  >
                    {loading.basic ? <CircularProgress size={16} /> : "Retry"}
                  </Button>
                </MDBox>
              </Card>
            ) : (
              <ComplexStatisticsCard
                color="warning"
                title={t("dashboard.pending")}
                icon={<Icon fontSize="large">schedule</Icon>}
                count={stats.basic?.pending?.toString() || "0"}
                percentage={{ color: "success", amount: "", label: "" }}
                isLoading={loading.basic}
              />
            )}
          </Grid>

          <Grid item xs={12} md={isAnnotator ? 3 : 2.4} lg={isAnnotator ? 3 : 2.4}>
            {errors.basic ? (
              <Card>
                <MDBox p={3} textAlign="center">
                  <Typography color="error">Failed to load stats</Typography>
                  <Button
                    size="small"
                    onClick={() => retry("basic")}
                    disabled={loading.basic}
                    sx={{ mt: 1 }}
                  >
                    {loading.basic ? <CircularProgress size={16} /> : "Retry"}
                  </Button>
                </MDBox>
              </Card>
            ) : (
              <ComplexStatisticsCard
                color="primary"
                title={t("dashboard.inProgress")}
                icon={<Icon fontSize="large">autorenew</Icon>}
                count={stats.basic?.in_progress?.toString() || "0"}
                percentage={{ color: "success", amount: "", label: "" }}
                isLoading={loading.basic}
              />
            )}
          </Grid>

          {isAnnotator ? (
            <Grid item xs={12} md={3} lg={3}>
              {errors.basic ? (
                <Card>
                  <MDBox p={3} textAlign="center">
                    <Typography color="error">Failed to load stats</Typography>
                    <Button
                      size="small"
                      onClick={() => retry("basic")}
                      disabled={loading.basic}
                      sx={{ mt: 1 }}
                    >
                      {loading.basic ? <CircularProgress size={16} /> : "Retry"}
                    </Button>
                  </MDBox>
                </Card>
              ) : (
                <ComplexStatisticsCard
                  color="success"
                  title={t("dashboard.completed")}
                  icon={<Icon fontSize="large">check_circle</Icon>}
                  count={stats.basic?.completed?.toString() || "0"}
                  percentage={{ color: "success", amount: "", label: "" }}
                  isLoading={loading.basic}
                />
              )}
            </Grid>
          ) : (
            <>
              <Grid item xs={12} md={2.4} lg={2.4}>
                {errors.basic ? (
                  <Card>
                    <MDBox p={3} textAlign="center">
                      <Typography color="error">Failed to load stats</Typography>
                      <Button
                        size="small"
                        onClick={() => retry("basic")}
                        disabled={loading.basic}
                        sx={{ mt: 1 }}
                      >
                        {loading.basic ? <CircularProgress size={16} /> : "Retry"}
                      </Button>
                    </MDBox>
                  </Card>
                ) : (
                  <ComplexStatisticsCard
                    color="success"
                    title={t("dashboard.annotatedOnce")}
                    icon={<Icon fontSize="large">looks_one</Icon>}
                    count={stats.basic?.annotated_once?.toString() || "0"}
                    percentage={{ color: "success", amount: "", label: "" }}
                    isLoading={loading.basic}
                  />
                )}
              </Grid>

              <Grid item xs={12} md={2.4} lg={2.4}>
                {errors.basic ? (
                  <Card>
                    <MDBox p={3} textAlign="center">
                      <Typography color="error">Failed to load stats</Typography>
                      <Button
                        size="small"
                        onClick={() => retry("basic")}
                        disabled={loading.basic}
                        sx={{ mt: 1 }}
                      >
                        {loading.basic ? <CircularProgress size={16} /> : "Retry"}
                      </Button>
                    </MDBox>
                  </Card>
                ) : (
                  <ComplexStatisticsCard
                    color="success"
                    title={t("dashboard.annotatedTwice")}
                    icon={<Icon fontSize="large">looks_two</Icon>}
                    count={stats.basic?.annotated_twice?.toString() || "0"}
                    percentage={{ color: "success", amount: "", label: "" }}
                    isLoading={loading.basic}
                  />
                )}
              </Grid>
            </>
          )}
        </Grid>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

// Main Dashboard Component with Language Provider
export default function Dashboard() {
  return <DashboardContent />;
}

// PropTypes for main Dashboard component
Dashboard.propTypes = {
  // Add any props your dashboard component receives
};
